import { useEffect, useState } from "react";

const STORAGE_KEY = "app.guidance-hidden";
const EVENT_NAME = "app:guidance-visibility";

/**
 * User preference for whether on-page setup-checklist guidance is
 * shown. Persisted to `localStorage` so the choice carries across page
 * loads. Same-tab updates fan out via a custom event so the top-bar
 * toggle and the in-page checklist re-render together; cross-tab
 * updates ride the native `storage` event.
 *
 * Default is visible (`hidden = false`).
 */
export function useGuidanceVisibility(): [boolean, (hidden: boolean) => void] {
  const [hidden, setHiddenState] = useState<boolean>(() => readInitial());

  useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      setHiddenState(detail);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setHiddenState(e.newValue === "true");
      }
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = (value: boolean) => {
    setHiddenState(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // localStorage may throw in private mode / disabled storage —
      // the in-memory state still works for the current page.
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
  };

  return [hidden, update];
}

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}
